/**
 * src/services/standaloneViewerAssets.ts
 *
 * Provides inline static assets embedded into the exported ZIP package
 * for 100% offline standalone viewing without web servers or build tools.
 */

export function generateStandaloneViewerHtml(tripName: string): string {
  const safeName = tripName.replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return `<!DOCTYPE html>
<html lang="it">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover" />
  <title>${safeName} — Travel Photo Map Standalone</title>
  <link rel="stylesheet" href="viewer.css" />
  <link rel="stylesheet" href="https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.css" />
  <script src="https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.js"></script>
  <!-- Dual-boot CORS-free data loader -->
  <script src="data.js"></script>
</head>
<body class="standalone-body">
  <!-- Header Bar -->
  <header class="standalone-header">
    <div class="brand">
      <div class="logo">🧭</div>
      <div>
        <h1 id="trip-title" class="trip-title">${safeName}</h1>
        <p id="trip-subtitle" class="trip-subtitle">Offline Standalone Package • Travel Photo Map</p>
      </div>
    </div>
    <div id="category-pills" class="category-pills"></div>
  </header>

  <!-- Interactive Map Canvas -->
  <main class="standalone-main">
    <div id="map" class="map-container"></div>

    <!-- Level-1 Map Popup Card -->
    <div id="popup-card" class="popup-card hidden">
      <img id="popup-img" class="popup-img" src="" alt="Thumbnail" />
      <div class="popup-info">
        <div class="popup-meta">
          <span id="popup-category" class="badge"></span>
          <span id="popup-time" class="time"></span>
        </div>
        <p id="popup-name" class="popup-name"></p>
        <p id="popup-step" class="popup-step"></p>
      </div>
      <div class="popup-action">🔍</div>
    </div>
  </main>

  <!-- Level-2 Lightbox Overlay -->
  <div id="lightbox" class="lightbox hidden">
    <div class="lightbox-top">
      <span id="lightbox-counter" class="lightbox-counter"></span>
      <button id="lightbox-close" class="lightbox-close" aria-label="Chiudi">✕</button>
    </div>
    <div class="lightbox-center">
      <img id="lightbox-img" class="lightbox-img" src="" alt="HD Preview" />
    </div>
    <div id="lightbox-exif" class="lightbox-bottom">
      <span id="exif-camera"></span>
      <span id="exif-exposure"></span>
      <span id="exif-lens"></span>
    </div>
  </div>

  <script src="viewer.js"></script>
</body>
</html>
`;
}

export function getStandaloneViewerCss(): string {
  return `/* Standalone Viewer Stylesheet */
* { box-sizing: border-box; margin: 0; padding: 0; }
body.standalone-body {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  background-color: #0f172a;
  color: #f8fafc;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.standalone-header {
  height: 56px;
  background-color: rgba(15, 23, 42, 0.95);
  backdrop-filter: blur(8px);
  border-bottom: 1px solid #1e293b;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 30;
  gap: 12px;
}
.brand { display: flex; align-items: center; gap: 10px; }
.logo {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background-color: rgba(14, 165, 233, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}
.trip-title { font-size: 14px; font-weight: 700; color: #fff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 260px; }
.trip-subtitle { font-size: 11px; color: #94a3b8; }
.category-pills { display: flex; align-items: center; gap: 6px; overflow-x: auto; max-width: 50vw; }
.pill {
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 9999px;
  background-color: #1e293b;
  color: #94a3b8;
  border: 1px solid #334155;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
}
.pill.active { background-color: #0284c7; color: #fff; border-color: #38bdf8; }
.standalone-main { flex: 1; position: relative; overflow: hidden; }
.map-container { width: 100%; height: 100%; }
.popup-card {
  position: absolute;
  bottom: 24px;
  left: 16px;
  right: 16px;
  max-width: 380px;
  margin: 0 auto;
  background-color: rgba(15, 23, 42, 0.95);
  backdrop-filter: blur(12px);
  border: 1px solid #334155;
  border-radius: 16px;
  padding: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  z-index: 40;
}
.popup-img { width: 64px; height: 64px; border-radius: 12px; object-fit: cover; border: 1px solid #334155; }
.popup-info { flex: 1; min-width: 0; }
.popup-meta { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
.badge { font-size: 10px; font-weight: 600; padding: 2px 8px; border-radius: 9999px; background-color: rgba(14, 165, 233, 0.2); color: #38bdf8; }
.time { font-size: 11px; color: #94a3b8; }
.popup-name { font-size: 12px; font-weight: 700; color: #fff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.popup-step { font-size: 11px; color: #94a3b8; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.popup-action { color: #38bdf8; font-weight: bold; font-size: 14px; padding-right: 4px; }
.lightbox {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.96);
  z-index: 50;
  display: flex;
  flex-direction: column;
}
.lightbox-top { height: 48px; padding: 0 16px; display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid #1e293b; background-color: rgba(0,0,0,0.5); }
.lightbox-counter { font-size: 12px; font-family: monospace; color: #94a3b8; }
.lightbox-close { background: none; border: none; color: #cbd5e1; font-size: 20px; cursor: pointer; padding: 8px; }
.lightbox-center { flex: 1; display: flex; align-items: center; justify-content: center; padding: 12px; }
.lightbox-img { max-width: 100%; max-height: 100%; object-fit: contain; }
.lightbox-bottom { padding: 12px; background-color: rgba(15, 23, 42, 0.85); backdrop-filter: blur(8px); border-top: 1px solid #1e293b; font-size: 12px; font-family: monospace; color: #94a3b8; display: flex; justify-content: space-around; }
.hidden { display: none !important; }
`;
}

export function getStandaloneViewerJs(): string {
  return `/**
 * Standalone Offline Travel Map Viewer Engine
 */
(async function() {
  let tripData = null;

  // 1. Dual-boot data loader: inspect window.__TRAVEL_DATA__ or fetch manifest
  if (window.__TRAVEL_DATA__) {
    tripData = window.__TRAVEL_DATA__;
  } else {
    try {
      const manifestRes = await fetch('./manifest.json');
      const manifest = await manifestRes.json();
      const [tripRes, photosRes] = await Promise.all([
        fetch(manifest.files.trip || './trip.json'),
        fetch(manifest.files.photos || './photos.json')
      ]);
      tripData = {
        manifest,
        trip: await tripRes.json(),
        photos: await photosRes.json()
      };
    } catch (e) {
      console.warn('Fallback loading data from local json files', e);
    }
  }

  if (!tripData || !tripData.photos) {
    document.getElementById('trip-title').innerText = 'Dati di viaggio non trovati';
    return;
  }

  const trip = tripData.trip || {};
  const photos = tripData.photos || [];

  document.getElementById('trip-title').innerText = trip.name || 'Travel Photo Map';
  document.getElementById('trip-subtitle').innerText = (photos.length) + ' foto • Standalone Package';

  // Category Pills
  const categories = ['Tutte', ...Array.from(new Set(photos.map(p => p.category).filter(Boolean)))];
  const pillsContainer = document.getElementById('category-pills');
  let activeCategory = 'Tutte';

  function renderPills() {
    pillsContainer.innerHTML = '';
    categories.forEach(cat => {
      const btn = document.createElement('button');
      btn.className = 'pill' + (cat === activeCategory ? ' active' : '');
      btn.innerText = cat;
      btn.onclick = () => {
        activeCategory = cat;
        renderPills();
        updateMarkers();
      };
      pillsContainer.appendChild(btn);
    });
  }
  renderPills();

  // Map & Markers Setup
  let map = null;
  const markers = [];
  let activePhoto = null;

  const popupCard = document.getElementById('popup-card');
  const popupImg = document.getElementById('popup-img');
  const popupCategory = document.getElementById('popup-category');
  const popupTime = document.getElementById('popup-time');
  const popupName = document.getElementById('popup-name');
  const popupStep = document.getElementById('popup-step');

  const lightbox = document.getElementById('lightbox');
  const lightboxImg = document.getElementById('lightbox-img');
  const lightboxCounter = document.getElementById('lightbox-counter');
  const lightboxClose = document.getElementById('lightbox-close');
  const exifCamera = document.getElementById('exif-camera');
  const exifExposure = document.getElementById('exif-exposure');
  const exifLens = document.getElementById('exif-lens');

  function showPopup(photo) {
    activePhoto = photo;
    popupImg.src = photo.thumbnailUrl || ('photos/thumb/' + photo.id + '.webp');
    popupCategory.innerText = photo.category || 'Foto';
    popupTime.innerText = photo.dateTimeOriginal ? photo.dateTimeOriginal.substring(0, 10) : '';
    popupName.innerText = photo.fileName || photo.id;
    popupStep.innerText = photo.stepName || '';
    popupCard.classList.remove('hidden');
  }

  function openLightbox(photo) {
    activePhoto = photo;
    lightboxImg.src = photo.previewUrl || ('photos/hd/' + photo.id + '.webp');
    const idx = photos.findIndex(p => p.id === photo.id);
    lightboxCounter.innerText = (idx + 1) + ' / ' + photos.length;
    exifCamera.innerText = photo.exif?.camera ? photo.exif.camera : '';
    exifExposure.innerText = photo.exif?.aperture ? (photo.exif.aperture + ' ' + (photo.exif.shutterSpeed || '')) : '';
    exifLens.innerText = photo.exif?.lens ? photo.exif.lens : '';
    lightbox.classList.remove('hidden');
  }

  popupCard.onclick = () => {
    if (activePhoto) openLightbox(activePhoto);
  };

  lightboxClose.onclick = () => {
    lightbox.classList.add('hidden');
  };

  function updateMarkers() {
    markers.forEach(m => m.remove());
    markers.length = 0;

    const filtered = photos.filter(p => {
      if (activeCategory !== 'Tutte' && p.category !== activeCategory) return false;
      return typeof p.lat === 'number' && typeof p.lon === 'number';
    });

    if (map) {
      filtered.forEach(p => {
        const el = document.createElement('div');
        el.style.width = '24px';
        el.style.height = '24px';
        el.style.borderRadius = '50%';
        el.style.backgroundColor = '#0284c7';
        el.style.border = '2px solid #ffffff';
        el.style.boxShadow = '0 2px 4px rgba(0,0,0,0.4)';
        el.style.cursor = 'pointer';
        el.onclick = (e) => {
          e.stopPropagation();
          showPopup(p);
        };
        const marker = new maplibregl.Marker({ element: el })
          .setLngLat([p.lon, p.lat])
          .addTo(map);
        markers.push(marker);
      });
    }
  }

  if (typeof maplibregl !== 'undefined') {
    const validCoords = photos.filter(p => typeof p.lat === 'number' && typeof p.lon === 'number');
    const center = validCoords.length > 0 ? [validCoords[0].lon, validCoords[0].lat] : [0, 20];
    
    map = new maplibregl.Map({
      container: 'map',
      style: 'https://tiles.openfreemap.org/styles/positron',
      center: center,
      zoom: 6
    });

    map.on('load', () => {
      updateMarkers();
    });
  }
})();
`;
}
